import React, { useEffect, useRef, useContext } from 'react';
import { useMap } from 'react-leaflet';
import * as L from 'leaflet';
import { GlobalContext } from '../../App';

const EditablePolygon = ({ field }) => {
  const map = useMap();
  const polygonRef = useRef(null);
  const { setSelectedUserAsset } = useContext(GlobalContext);

  useEffect(() => {
    if (!field || !field.geometry || !field.geometry.coordinates) return;

    // Remove existing polygon
    if (polygonRef.current) {
      map.removeLayer(polygonRef.current);
    }

    // Convert coordinates to Leaflet format [lat, lng]
    const leafletCoords = field.geometry.coordinates[0].map(coord => [coord[1], coord[0]]);

    // Create polygon with high z-index to appear above TCI
    const polygon = L.polygon(leafletCoords, {
      color: '#1890ff',
      fillColor: '#1890ff',
      fillOpacity: 0.2,
      weight: 3
    }).addTo(map);
    
    // Ensure polygon appears above TCI layers
    polygon.setStyle({ pane: 'overlayPane' });
    if (polygon.bringToFront) {
      polygon.bringToFront();
    }

    // Enable editing
    if (polygon.editing) {
      polygon.editing.enable();
    }

    // Custom drag functionality
    let isDragging = false;
    let dragStartPoint = null;
    let originalLatLngs = null;

    const startDrag = (e) => {
      if (e.originalEvent.ctrlKey || e.originalEvent.metaKey) { // Only drag when Ctrl/Cmd is held
        isDragging = true;
        dragStartPoint = e.latlng;
        originalLatLngs = polygon.getLatLngs()[0].map(ll => L.latLng(ll.lat, ll.lng));
        map.dragging.disable();
        e.originalEvent.preventDefault();
      }
    };

    const doDrag = (e) => {
      if (isDragging && dragStartPoint) {
        const latDiff = e.latlng.lat - dragStartPoint.lat;
        const lngDiff = e.latlng.lng - dragStartPoint.lng;
        
        const newLatLngs = originalLatLngs.map(ll => 
          L.latLng(ll.lat + latDiff, ll.lng + lngDiff)
        );
        
        polygon.setLatLngs(newLatLngs);
      }
    };

    const endDrag = () => {
      if (isDragging) {
        isDragging = false;
        dragStartPoint = null;
        originalLatLngs = null;
        map.dragging.enable();
        updateGeometry();
      }
    };

    // Handle polygon changes (vertex editing)
    const handleEdit = () => {
      updateGeometry();
    };

    const updateGeometry = () => {
      try {
        const updatedCoords = polygon.getLatLngs()[0].map(latlng => [latlng.lng, latlng.lat]);
        updatedCoords.push(updatedCoords[0]); // Close the polygon
        
        const updatedGeometry = {
          type: 'Polygon',
          coordinates: [updatedCoords]
        };

        setSelectedUserAsset({
          ...field,
          geometry: updatedGeometry
        });
      } catch (error) {
        console.error('Error updating polygon:', error);
      }
    };

    // Listen for edit and custom drag events
    polygon.on('edit', handleEdit);
    polygon.on('mousedown', startDrag);
    map.on('mousemove', doDrag);
    map.on('mouseup', endDrag);

    polygonRef.current = polygon;

    // Fit map to polygon bounds
    try {
      map.fitBounds(polygon.getBounds(), { padding: [20, 20] });
    } catch (error) {
      console.error('Error fitting bounds:', error);
    }

    return () => {
      if (polygonRef.current) {
        try {
          // Remove event listeners
          map.off('mousemove', doDrag);
          map.off('mouseup', endDrag);
          map.removeLayer(polygonRef.current);
          map.dragging.enable(); // Ensure map dragging is re-enabled
        } catch (error) {
          console.error('Error removing layer:', error);
        }
      }
    };
  }, [field, map, setSelectedUserAsset]);

  return null;
};

export default EditablePolygon;